'use strict';

var FirebaseExt = require('./FirebaseExt');
var FirebaseStorage = require('./FirebaseStorage');
var KayleeBase = require('./KayleeBase');
var KayleeConnectionState = require('./KayleeConnectionState');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('FirebaseObject');
var sutl = require('../sutl/Sutl');
var TaskQueue = require('./TaskQueue');

// These are global so we count $set calls across all FirebaseObjects.

var offlineSetCompletedCount = 0;
var onlineSetFailedCount = 0;

function FirebaseObject(ref) {
    var refString = ref.toString();

    this.$ref = ref;
    this.$id = ref.key;
    this.$internalIsLoaded = false;
    this.$loadedPromises = [];
    this.$preChangeListeners = [];
    this.$postChangeListeners = [];
    this.$destroyListeners = [];
    this.$savePromises = [];
    this.$storageRefs = {};

    if (KayleeConnectionState.isWorkingOnline()) {
        this.$initStorage(this.$storage);
        ref.on('value', this.$valueChangedCb, this.$cancelledCb, this);
    }

    log.debug('created ' + refString);
}

FirebaseObject.$intFromJson = function (data, name, reqProps, exclProps, create) {
    var missingProps = [];

    for (var i = 0; i < reqProps.length; i++) {
        if (!data.hasOwnProperty(reqProps[i])) missingProps.push(reqProps[i]);
    }

    if (missingProps.length > 0) {
        log.error('fromJson: ' + name + ': missing props: ' + JSON.stringify(missingProps));
        return null;
    }

    var obj = create(data);

    for (var prop in data) {
        if (!data.hasOwnProperty(prop)) continue;
        if (exclProps.includes(prop)) continue;
        obj[prop] = data[prop];
    }

    return obj;
};

FirebaseObject.$fromJson = function (data, name, reqProps, exclProps, create) {
    exclProps = exclProps.concat(['$nodeType']);
    return FirebaseObject.$intFromJson(data, name, reqProps, exclProps, create);
};

FirebaseObject.$copyObjectWithStorage = function (obj, storage) {
    if (typeof storage !== 'object') return obj.key;

    var data = {};

    for (var prop in obj) {
        if (!obj.hasOwnProperty(prop)) continue;

        if (storage.hasOwnProperty('*') || storage.hasOwnProperty(prop)) {
            var key = FirebaseObject.$copyObjectWithStorage(obj[prop], storage[prop]);
            if (key !== null) data[prop] = key;
        } else {
            data[prop] = JSON.parse(JSON.stringify(obj[prop]));
        }
    }

    return data;
};

FirebaseObject.$intToJson = function (obj, preserveProps, addData) {
    var data = {};
    var prop;

    for (prop in obj) {
        /* jshint forin: false */ // if (!obj.hasOwnProperty(prop)) continue;
        if (typeof obj[prop] === 'function') continue;
        if (prop.charAt(0) === '$' && !preserveProps.includes(prop)) continue;

        if (typeof obj[prop] === 'object') {
            if (obj.$storage && obj.$storage.hasOwnProperty(prop)) {
                data[prop] = FirebaseObject.$copyObjectWithStorage(obj[prop], obj.$storage[prop]);

                if (Object.keys(data[prop]).length === 0) {
                    delete data[prop];
                }
            } else {
                data[prop] = JSON.parse(JSON.stringify(obj[prop]));
            }
        } else {
            data[prop] = obj[prop];
        }
    }

    for (prop in addData) {
        if (!addData.hasOwnProperty(prop)) continue;
        data[prop] = addData[prop];
    }

    return data;
};

FirebaseObject.prototype.$toJson = function (preserveProps, addData) {
    preserveProps = preserveProps.concat(['$nodeType', '$internalIsLoaded']);
    return FirebaseObject.$intToJson(this, preserveProps, addData);
};

FirebaseObject.prototype.$initStorage = function (storage, path) {
    if (typeof storage === 'undefined') return;
    if (typeof path === 'undefined') path = [];

    for (var prop in storage) {
        if (!storage.hasOwnProperty(prop)) continue;
        if (prop === '*') continue; // wildcards handled after load
        path.push(prop);
        if (typeof storage[prop] === 'object') {
            this.$initStorage(storage[prop], path);
        } else {
            var pathStr =
                this.$ref.toString().replace(this.$ref.root.toString(), '') + '/' + path.join('/');
            var storageRef = new FirebaseStorage(pathStr);
            sutl.setNode(this.$storageRefs, path, storageRef);
            sutl.setNode(this, path, storageRef);
        }
        path.pop();
    }
};

FirebaseObject.prototype.$removeDeletedProps = function (newData, currData, path) {
    if (typeof currData === 'undefined') currData = this;
    if (typeof path === 'undefined') path = [];

    for (var prop in currData) {
        if (!currData.hasOwnProperty(prop)) continue;
        if (prop.charAt(0) === '$') continue;

        path.push(prop);

        if (currData[prop] !== null && typeof currData[prop] === 'object') {
            if (currData[prop] instanceof FirebaseStorage) {
                if (currData[prop].isWildcard) {
                    delete currData[prop];
                    sutl.deleteNode(this.$storageRefs, path);
                } else {
                    if (!newData.hasOwnProperty(prop)) {
                        currData[prop].updateKey(null);
                    }
                }
            } else if (!newData.hasOwnProperty(prop)) {
                delete currData[prop];
            } else {
                this.$removeDeletedProps(newData[prop], currData[prop], path);
            }
        } else {
            if (!newData.hasOwnProperty(prop)) delete currData[prop];
        }

        if (!newData.hasOwnProperty(prop)) {
            delete currData[prop];
        }

        path.pop(prop);
    }
};

FirebaseObject.prototype.$copyDefaults = function (dst, src) {
    var keys = Object.keys(src);

    for (var i = 0, ii = keys.length; i < ii; i++) {
        var key = keys[i];
        var value = src[key];

        // Note, we don't support Date, RegExp, Array, or other complex types
        // other than values and simple objects. We shouldn't need them for
        // defaults. For efficiency, we don't even check for those types here
        // and throw an error.

        if (value !== null && typeof value === 'object') {
            if (!dst.hasOwnProperty(key)) {
                dst[key] = JSON.parse(JSON.stringify(value));
            } else {
                this.$copyDefaults(dst[key], value);
            }
        } else {
            if (!dst.hasOwnProperty(key)) dst[key] = value;
        }
    }
};

FirebaseObject.prototype.$applyDefaults = function () {
    if ('$defaults' in this) this.$copyDefaults(this, this.$defaults);
};

FirebaseObject.prototype.$copyProps = function (data) {
    for (var prop in data) {
        if (!data.hasOwnProperty(prop)) continue;
        if (typeof data[prop] === 'object') {
            this[prop] = JSON.parse(JSON.stringify(data[prop]));
        } else {
            this[prop] = data[prop];
        }
    }
};

FirebaseObject.prototype.$convertStorageProps = function (data, storage, path, isWildcard) {
    if (!('$storage' in this)) return;
    if (typeof storage === 'undefined') storage = this.$storage;
    if (typeof path === 'undefined') path = [];
    if (typeof isWildcard === 'undefined') isWildcard = false;

    for (var prop in storage) {
        if (!storage.hasOwnProperty(prop)) continue;

        if (prop === '*') {
            isWildcard = true;
            var obj = sutl.getNode(data, path, {});
            for (var key in obj) {
                if (!obj.hasOwnProperty(key)) continue;
                path.push(key);
                this.$convertStorageProps(data, storage[prop], path, isWildcard);
                path.pop();
            }
        } else if (typeof storage[prop] === 'object') {
            path.push(prop);
            this.$convertStorageProps(data, storage[prop], path, isWildcard);
            path.pop();
        } else {
            path.push(prop);
            var newValue = sutl.getNode(data, path);
            var storageRef = sutl.getNode(this.$storageRefs, path);

            if (isWildcard) {
                if (storageRef === null) {
                    var pathStr =
                        this.$ref.toString().replace(this.$ref.root.toString(), '') +
                        '/' +
                        path.join('/');
                    storageRef = new FirebaseStorage(pathStr, newValue, isWildcard);
                    sutl.setNode(this.$storageRefs, path, storageRef);
                }
                sutl.setNode(this, path, storageRef);
            } else {
                if (newValue !== storageRef.key) storageRef.updateKey(newValue);
                sutl.setNode(this, path, storageRef);
            }

            path.pop();
        }
    }
};

FirebaseObject.prototype.$putNewStorageWildcard = function (path, data, metadata) {
    if (typeof path === 'string') path = path.split('.');

    if (!('$storage' in this)) {
        log.error('no storage defined');
        return Promise.reject('no storage defined');
    }

    var storage = this.$storage;

    for (var i = 0; i < path.length; i++) {
        var p = path[i];

        if ('*' in storage) {
            storage = storage['*'];
        } else if (p in storage) {
            storage = storage[p];
        } else {
            log.error('invalid storage path');
            return Promise.reject('invalid storage path');
        }
    }

    if (typeof storage !== 'boolean' || !storage) {
        log.error('invalid storage path');
        return Promise.reject('invalid storage path');
    }

    var pathStr =
        this.$ref.toString().replace(this.$ref.root.toString(), '') + '/' + path.join('/');
    var storageRef = new FirebaseStorage(pathStr, null, true);

    if (sutl.getNode(this.$storageRefs, path) !== null) {
        log.error('storage ref already exists at path');
        return Promise.reject('storage ref already exists at path');
    }

    sutl.setNode(this.$storageRefs, path, storageRef);
    sutl.setNode(this, path, storageRef);
    return storageRef.put(data, metadata);
};

FirebaseObject.prototype.$destroy = function (error) {
    log.debug('destroying ' + this.$ref);

    this.$notifyOnDestroyListeners();

    if (KayleeConnectionState.isWorkingOnline()) {
        this.$ref.off('value', this.$valueChangedCb, this);
    }

    this.$internalIsLoaded = false;
    if (typeof error === 'undefined') {
        this.$rejectLoadedPromises(new Error('object destroyed'));
    } else {
        this.$rejectLoadedPromises(error);
    }
    this.$preChangeListeners.length = 0;
    this.$postChangeListeners.length = 0;
    this.$destroyListeners.length = 0;
    FirebaseExt.update();
};

FirebaseObject.prototype.$valueChangedCb = function (snap) {
    if (snap.exists()) {
        var data = snap.val();
        this.$notifyPreChangeListeners(data);

        if (typeof data !== 'object') {
            this.$removeDeletedProps({});
            this.$value = data;
        } else {
            delete this.$value;
            this.$removeDeletedProps(data);
            this.$copyProps(data);
            this.$convertStorageProps(data);
            this.$applyDefaults();
        }

        this.$notifyPostChangeListeners();
    } else {
        if (this.$supportEmptyObject) this.$notifyPreChangeListeners({});
        this.$removeDeletedProps({});
        if (this.$supportEmptyObject) this.$notifyPostChangeListeners({});
    }

    if (!this.$internalIsLoaded) {
        this.$internalIsLoaded = true;
        this.$resolveLoadedPromises();
    }

    FirebaseExt.update();
};

FirebaseObject.prototype.$cancelledCb = function (error) {
    //log.debug('$cancelledCb: ' + this.$ref.toString() + ', error: ' + error);
    this.$destroy(error);
};

FirebaseObject.prototype.$notifyPreChangeListeners = function (data) {
    for (var i = 0; i < this.$preChangeListeners.length; i++) {
        var listener = this.$preChangeListeners[i];
        if (listener.context) {
            listener.callback.call(listener.context, this, data);
        } else {
            listener.callback(this, data);
        }
    }
};

FirebaseObject.prototype.$notifyPostChangeListeners = function () {
    for (var i = 0; i < this.$postChangeListeners.length; i++) {
        var listener = this.$postChangeListeners[i];
        if (listener.context) {
            listener.callback.call(listener.context, this);
        } else {
            listener.callback(this);
        }
    }
};

FirebaseObject.prototype.$onChange = function (listener, context, initialCall) {
    if (typeof initialCall === 'undefined') initialCall = true;
    if (typeof context === 'undefined') context = null;
    this.$postChangeListeners.push({ callback: listener, context: context });
    if (this.$internalIsLoaded && initialCall) {
        if (context) {
            listener.call(context, this);
        } else {
            listener(this);
        }
    }
};

FirebaseObject.prototype.$offChange = function (listener, context) {
    if (typeof context === 'undefined') context = null;
    for (var i = 0; i < this.$postChangeListeners.length; i++) {
        if (
            this.$postChangeListeners[i].callback === listener &&
            this.$postChangeListeners[i].context === context
        ) {
            this.$postChangeListeners.splice(i, 1);
            break;
        }
    }
};

FirebaseObject.prototype.$onBeforeChange = function (listener, context) {
    this.$preChangeListeners.push({ callback: listener, context: context });
};

FirebaseObject.prototype.$offBeforeChange = function (listener, context) {
    if (typeof context === 'undefined') context = null;
    for (var i = 0; i < this.$preChangeListeners.length; i++) {
        if (
            this.$preChangeListeners[i].callback === listener &&
            this.$preChangeListeners[i].context === context
        ) {
            this.$preChangeListeners.splice(i, 1);
            break;
        }
    }
};

FirebaseObject.prototype.$isLoaded = function () {
    return this.$internalIsLoaded;
};

FirebaseObject.prototype.$loaded = function () {
    if (this.$internalIsLoaded) return Promise.resolve(this);

    var deferredPromise = {};
    deferredPromise.promise = new Promise(function (resolve, reject) {
        deferredPromise.resolve = resolve;
        deferredPromise.reject = reject;
    });

    this.$loadedPromises.push(deferredPromise);

    return deferredPromise.promise;
};

FirebaseObject.prototype.$resolveLoadedPromises = function () {
    for (var i = 0; i < this.$loadedPromises.length; i++) {
        this.$loadedPromises[i].resolve(this);
    }

    this.$loadedPromises.length = 0;
};

FirebaseObject.prototype.$rejectLoadedPromises = function (reason) {
    for (var i = 0; i < this.$loadedPromises.length; i++) {
        this.$loadedPromises[i].reject(reason);
    }

    this.$loadedPromises.length = 0;
};

FirebaseObject.prototype.$onDestroy = function (listener) {
    this.$destroyListeners.push(listener);
};

FirebaseObject.prototype.$offDestroy = function (listener) {
    for (var i = 0; i < this.$destroyListeners.length; i++) {
        if (this.$destroyListeners[i] === listener) {
            this.$destroyListeners.splice(i, 1);
        }
    }
};

FirebaseObject.prototype.$notifyOnDestroyListeners = function () {
    for (var i = 0; i < this.$destroyListeners.length; i++) {
        this.$destroyListeners[i](this);
    }
};

FirebaseObject.prototype.$getKeys = function () {
    var keys = Object.keys(this);
    var dataKeys = [];

    for (var i = 0; i < keys.length; i++) {
        if (this.hasOwnProperty(keys[i]) && keys[i].charAt(0) !== '$') {
            dataKeys.push(keys[i]);
        }
    }

    return dataKeys;
};

FirebaseObject.prototype.$copyDatabaseProperties = function (dest, src, path) {
    if (typeof src === 'undefined') src = this;
    if (typeof path === 'undefined') path = [];

    for (var prop in src) {
        if (!src.hasOwnProperty(prop)) continue;
        if (prop.charAt(0) === '$') continue;

        path.push(prop);

        if (src[prop] instanceof FirebaseStorage) {
            if (src[prop].key !== null) {
                dest[prop] = src[prop].key;
            }
        } else if (Array.isArray(src[prop])) {
            dest[prop] = sutl.deepCopy(src[prop]);
        } else if (typeof src[prop] === 'object' && src[prop] !== null) {
            dest[prop] = {};
            this.$copyDatabaseProperties(dest[prop], src[prop], path);
        } else {
            dest[prop] = src[prop];
        }

        path.pop();
    }

    return dest;
};

FirebaseObject.prototype.$addNullUpdates = function (oldData, newData, updates, path) {
    if (typeof path === 'undefined') {
        path = this.$ref.toString().replace(this.$ref.root, '');
    }

    if (typeof newData !== 'object') return;

    for (var prop in oldData) {
        if (!oldData.hasOwnProperty(prop)) continue;

        var newPath = path + '/' + prop;

        if (!newData.hasOwnProperty(prop)) {
            updates[newPath] = null;
        } else if (typeof oldData[prop] === 'object') {
            this.$addNullUpdates(oldData[prop], newData[prop], updates, newPath);
        }
    }

    return updates;
};

FirebaseObject.prototype.$removeUnchangedProps = function (oldData, newData, objProps) {
    if (typeof objProps === 'undefined') objProps = {};

    for (var prop in newData) {
        if (!newData.hasOwnProperty(prop)) continue;
        // allow missing properties (e.g., for the end property of the call object)
        if (!oldData.hasOwnProperty(prop)) continue;

        if (newData[prop] !== null && typeof newData[prop] === 'object') {
            if (typeof objProps[prop] === 'boolean' && objProps[prop]) {
                if (sutl.objEquals(oldData[prop], newData[prop])) {
                    delete newData[prop];
                }
            } else {
                this.$removeUnchangedProps(oldData[prop], newData[prop], objProps[prop]);
                if (Object.keys(newData[prop]).length <= 0) delete newData[prop];
            }
        } else {
            if (oldData[prop] === newData[prop]) {
                delete newData[prop];
            }
        }
    }
};

FirebaseObject.prototype.$addUpdates = function (updates, defaults, cantSave, props, path) {
    if (typeof path === 'undefined') {
        path = this.$ref.toString().replace(this.$ref.root, '');
    }

    if (typeof defaults === 'undefined') defaults = {};
    if (typeof cantSave === 'undefined') cantSave = {};

    for (var prop in props) {
        if (!props.hasOwnProperty(prop)) continue;

        var newPath = path + '/' + prop;

        if (props[prop] !== null && typeof props[prop] === 'object') {
            this.$addUpdates(updates, defaults[prop], cantSave[prop], props[prop], newPath);
        } else {
            if (cantSave[prop]) {
                log.error("can't save to " + newPath + ', excluding from update');
            } else {
                updates[newPath] = props[prop];
            }
        }
    }

    return updates;
};

FirebaseObject.prototype.$removeExcludedProps = function (props, includeProps) {
    if (typeof includeProps === 'undefined') includeProps = {};

    for (var prop in props) {
        if (!props.hasOwnProperty(prop)) continue;

        if (props[prop] !== null && typeof props[prop] === 'object') {
            if (includeProps[prop] !== null && typeof includeProps[prop] === 'object') {
                this.$removeExcludedProps(props[prop], includeProps[prop]);
                if (Object.keys(props[prop]).length === 0) delete props[prop];
            } else {
                if (!includeProps[prop]) delete props[prop];
            }
        } else {
            if (!includeProps[prop]) delete props[prop];
        }
    }
};

FirebaseObject.prototype.$addRemovedItems = function (oldData, newData, changedData, path) {
    if (typeof path === 'undefined') {
        if (!oldData.hasOwnProperty('config')) return;

        if (!newData.hasOwnProperty('config')) {
            changedData.config = { removed: true };
            return;
        }

        path = ['config'];
        this.$addRemovedItems(oldData.config, newData.config, changedData, path);
        return;
    }

    if (
        typeof oldData === 'undefined' ||
        typeof oldData !== 'object' ||
        typeof newData !== 'object'
    )
        return;

    for (var prop in oldData) {
        if (!oldData.hasOwnProperty(prop)) continue;
        path.push(prop);
        if (newData.hasOwnProperty(prop)) {
            this.$addRemovedItems(oldData[prop], newData[prop], changedData, path);
        } else {
            var p = changedData;
            var includeProps = this.$includeInChangedEvent;
            var include = false;
            var i;

            if (typeof includeProps === 'undefined') includeProps = {};

            for (i = 0; i < path.length; i++) {
                if (!includeProps.hasOwnProperty(path[i])) break;

                includeProps = includeProps[path[i]];

                if (includeProps === true) {
                    include = true;
                    break;
                }
            }

            if (include) {
                for (i = 0; i < path.length; i++) {
                    if (!p.hasOwnProperty(path[i])) p[path[i]] = {};
                    if (i < path.length - 1) {
                        p = p[path[i]];
                    } else {
                        p[path[i]].removed = true;
                    }
                }
            }
        }
        path.pop();
    }
};

FirebaseObject.prototype.$processUpdateForSave = function () {
    var self = this;
    return self.$ref.once('value').then(function (snap) {
        var updates = {};
        var oldData = snap.exists() ? snap.val() : {};
        if ('$defaults' in self) self.$copyDefaults(oldData, self.$defaults);
        var newData = self.$copyDatabaseProperties({});
        self.$addNullUpdates(oldData, newData, updates);
        var changedData = JSON.parse(JSON.stringify(newData));
        self.$removeUnchangedProps(oldData, changedData, self.$objProps);
        self.$addUpdates(updates, self.$defaults, self.$cantSave, changedData);
        self.$removeExcludedProps(changedData, self.$includeInChangedEvent);
        self.$addRemovedItems(oldData, newData, changedData);

        return {
            current: oldData, // the values before the update is applied
            changed: changedData, // the values after the update is applied
            updates: updates, // the updates to apply
        };
    });
};

FirebaseObject.prototype.$removeSavePromise = function (savePromise) {
    for (var i = 0; i < this.$savePromises.length; i++) {
        if (this.$savePromises[i].id === savePromise.id) {
            this.$savePromises.splice(i, 1);
            return;
        }
    }

    log.error('no savePromise with id ' + savePromise.id + ' for ' + this);
};

FirebaseObject.prototype.$save = function (userInitiated, logError, onBeforeSave) {
    if (typeof logError === 'undefined') logError = true;

    var preSave = function () {
        if (typeof onBeforeSave === 'function') onBeforeSave();
        return Promise.resolve();
    };

    var self = this;
    var savePromise;
    var id;

    if (self.$savePromises.length === 0) {
        id = sutl.generateFirebaseId();
        savePromise = { id: id, userInitiated: userInitiated, logError: logError };
        self.$savePromises.push(savePromise);
        savePromise.promise = preSave()
            .then(function () {
                return self.$saveInternal(userInitiated, logError);
            })
            .then(function (result) {
                self.$removeSavePromise(savePromise);
                return result;
            });
    } else if (self.$savePromises.length === 1 || typeof onBeforeSave !== 'undefined') {
        id = sutl.generateFirebaseId();
        savePromise = { id: id, userInitiated: userInitiated, logError: logError };
        self.$savePromises.push(savePromise);
        savePromise.promise = self.$savePromises[self.$savePromises.length - 2].promise
            .then(preSave)
            .then(function () {
                return self.$saveInternal(userInitiated, logError);
            })
            .then(function (result) {
                self.$removeSavePromise(savePromise);
                return result;
            });
    } else {
        savePromise = self.$savePromises[self.$savePromises.length - 1];

        if (savePromise.userInitiated !== userInitiated) {
            log.warning(
                '$save: userInitiated ' +
                    savePromise.userInitated +
                    ' !== ' +
                    userInitiated +
                    ' for ' +
                    self
            );
        }

        if (savePromise.logError !== logError) {
            log.warning(
                '$save: logError ' + savePromise.logError + ' !== ' + logError + ' for ' + self
            );
        }
    }

    return savePromise.promise;
};

FirebaseObject.prototype.$saveInternal = function (userInitiated, logError) {
    if (typeof logError === 'undefined') logError = true;
    var self = this;
    var msg;

    if (!KayleeConnectionState.isWorkingOnline()) {
        return KayleeUtil.timeout(null, 1).then(function () {
            self.$notifyPreChangeListeners(self);
            self.$notifyPostChangeListeners();
            return Promise.resolve();
        });
    }

    if (KayleeBase.app.auth().currentUser === null) {
        msg = "not authenticated, can't save " + self;
        log.error(msg);
        return Promise.reject(new Error(msg));
    }

    if (!self.$internalIsLoaded) {
        msg = self + ' not loaded';
        log.error(msg);
        return Promise.reject(new Error(msg));
    }

    var updateRef = null;

    return self
        .$processUpdateForSave()
        .then(function (update) {
            if (typeof self.$onSave === 'function') {
                // TODO: remove this, I don't think it is used
                self.$onSave(update);
            }

            if (Object.keys(update.updates).length <= 0) {
                //log.debug('nothing to do for $save on ' + self);
                return Promise.resolve();
            }

            return Promise.resolve()
                .then(function () {
                    if (
                        typeof self.$addUpdatesForChangedEvent === 'undefined' ||
                        Object.keys(update.changed).length === 0
                    ) {
                        return Promise.resolve(update.updates);
                    }

                    return self.$addUpdatesForChangedEvent(
                        update.updates,
                        self.$id,
                        update.current,
                        update.changed,
                        userInitiated
                    );
                })
                .then(function (update) {
                    updateRef = update;
                    //log.debug('jsj: ' + self + ' update = ' + JSON.stringify(update, null, 4));
                    return self.$ref.root.update(update);
                });
        })
        .catch(function (err) {
            if (updateRef !== null) {
                log.debug('update = ' + JSON.stringify(updateRef, null, 4));
            }

            if (logError) {
                log.error('$save on ' + self + ' failed, error: ' + err);
            } else {
                log.debug('$save on ' + self + ' failed, error: ' + err);
            }

            return Promise.reject(err);
        });
};

FirebaseObject.prototype.$getValues = function () {
    return this.$copyDatabaseProperties({});
};

// Profiling for FirebaseObject.$set(). See SNTY-2830.

//var timingCount = 0;
//var timingData = {};
//var timingProps = [
//    'getValues',
//    'addNullUpdates',
//    'copy',
//    'removeUnchangedProps',
//    'addUpdates',
//    'removeExcludedProps',
//    'addRemovedItems',
//    'addNewData',
//    'addOldData',
//    'misc',
//    'update',
//];
//
//
//setInterval(function() {
//    var total = 0;
//
//    for (var p of timingProps) {
//        var x = timingData[p];
//        if (x !== undefined) {
//            total += x;
//        }
//    }
//
//    if (total === 0) total = 1;
//
//    log.debug('jsj: ------- Timing Data (' + timingCount + ' invocations) -------');
//
//    for (var prop of timingProps) {
//        var name = prop.padEnd(20, ' ');
//        var value = timingData[prop];
//        if (value === undefined) continue;
//        var msValue = value.toString().padStart(6, ' ');
//        var perValue = (value / timingCount).toFixed(1).padStart(6, ' ');
//        var pctValue = (100 * value / total).toFixed(1).padStart(4, ' ');
//        log.debug('jsj: ' + name + ' ' + msValue + ' ms   ' + perValue + ' ms   ' + pctValue + ' %');
//    }
//}, 10000);
//
//function profile(prop, prevTimestamp) {
//    var newTimestamp = Date.now();
//    if (timingData[prop] === undefined) timingData[prop] = 0;
//    timingData[prop] += newTimestamp - prevTimestamp;
//    return newTimestamp;
//}

// Replacement for JSON.parse(JSON.stringify(value)). That is 57% faster.
//
// Performs a deep copy/clone that doesn't preserve keys with undefined values or empty objects.

function cleanDeepCopy(orig) {
    if (orig === null) return orig;

    if (Array.isArray(orig)) {
        var arr = [];

        for (var a of orig) {
            arr.push(cleanDeepCopy(a));
        }

        return arr;
    }

    if (typeof orig === 'object') {
        var obj = {};

        for (var key of Object.keys(orig)) {
            var value = orig[key];
            if (value === undefined) continue;
            var newValue = cleanDeepCopy(orig[key]);
            if (typeof newValue === 'object' &&
                newValue !== null &&
                Object.keys(newValue).length === 0) {
                continue;
            }
            obj[key] = newValue;
        }

        return obj;
    }

    return orig;
}

FirebaseObject.prototype.$doOfflineSet = function (updates) {
    if (Object.keys(updates).length <= 0) return Promise.resolve();

    var self = this;
    var offlineTimeoutMs = KayleeConnectionState.getOfflineTimeoutMs();

    if (
        offlineTimeoutMs > 0 &&
        !KayleeConnectionState.isWorkingOnline() &&
        KayleeConnectionState.isConnected()
    ) {
        Promise.race([
            self.$ref.root.update(updates).then(function() { return true; }),
            KayleeUtil.timeout(false, offlineTimeoutMs),
        ]).then(function(completed) {
            if (!KayleeConnectionState.isWorkingOnline()) {
                if (completed) {
                    offlineSetCompletedCount++;

                    if (offlineSetCompletedCount >= 3) {
                        log.warning('$set operations are completing, going online');
                        onlineSetFailedCount = 0;
                        KayleeConnectionState.setWorkingOnline(true);
                    }
                } else {
                    offlineSetCompletedCount = 0;
                }
            } else {
                onlineSetFailedCount = 0;
            }
        });
    }

    self.$notifyPreChangeListeners(self);
    self.$notifyPostChangeListeners();
    return Promise.resolve();
};

// Based on $save, but doesn't require the initial snapshot to be retrieved. This avoids the
// reentrancy and race problems when performing updates in quick succession (as we were seeing in
// EquipmentService).

FirebaseObject.prototype.$set = function (newData, userInitiated, doChangedEvent) {
    if (doChangedEvent === undefined) doChangedEvent = true;

    var self = this;
    var updates = {};
    //if (!doChangedEvent) timingCount++;
    //var timestamp = Date.now();
    var oldData = self.$getValues();
    //if (!doChangedEvent) timestamp = profile('getValues', timestamp);
    //if ('$defaults' in this) self.$copyDefaults(oldData, self.$defaults);
    self.$addNullUpdates(oldData, newData, updates);
    //if (!doChangedEvent) timestamp = profile('addNullUpdates', timestamp);
    //var changedData = JSON.parse(JSON.stringify(newData));
    var changedData = cleanDeepCopy(newData);
    //if (!doChangedEvent) timestamp = profile('copy', timestamp);
    self.$removeUnchangedProps(oldData, changedData, self.$objProps);
    //if (!doChangedEvent) timestamp = profile('removeUnchangedProps', timestamp);
    self.$addUpdates(updates, self.$defaults, self.$cantSave, changedData);
    //if (!doChangedEvent) timestamp = profile('addUpdates', timestamp);
    self.$removeExcludedProps(changedData, self.$includeInChangedEvent);
    //if (!doChangedEvent) timestamp = profile('removeExcludedProps', timestamp);
    self.$addRemovedItems(oldData, newData, changedData);
    //if (!doChangedEvent) timestamp = profile('addRemovedItems', timestamp);
    var prop;

    // Apply new values to this.

    for (prop in newData) {
        if (!newData.hasOwnProperty(prop)) continue;
        self[prop] = newData[prop];
    }

    //if (!doChangedEvent) timestamp = profile('addNewData', timestamp);

    for (prop in oldData) {
        if (!oldData.hasOwnProperty(prop)) continue;
        if (!newData.hasOwnProperty(prop)) {
            delete self[prop];
        }
    }

    //if (!doChangedEvent) timestamp = profile('addOldData', timestamp);

    // Do the database updates as in $save.

    var msg;

    if (!KayleeConnectionState.isWorkingOnline()) {
        return self.$doOfflineSet(updates);
    }

    if (KayleeBase.app.auth().currentUser === null) {
        msg = "not authenticated, can't save " + self;
        log.error(msg);
        return Promise.reject(new Error(msg));
    }

    if (!self.$internalIsLoaded) {
        msg = self + ' not loaded';
        return Promise.reject(new Error(msg));
    }

    //if (!doChangedEvent) profile('misc', timestamp);

    // No need to call $processUpdateForSave(). We computed the information at the beginning of
    // this function.
    //
    // current: oldData
    // changed: changedData
    // updates: updates

    var eventUpdates = {};

    if (Object.keys(updates).length <= 0) return Promise.resolve();

    return TaskQueue.execute(function() {
        return Promise.resolve()
            .then(function () {
                //timestamp = Date.now();
                //var foo = self.$ref.root.update(updates);
                //if (!doChangedEvent) profile('update', timestamp);
                //return foo;

                if (!KayleeConnectionState.isWorkingOnline()) {
                    // This can happen if we've queued up a bunch of updates and then go offline.
                    doChangedEvent = false;
                    return self.$doOfflineSet(updates);
                }

                var offlineTimeoutMs = KayleeConnectionState.getOfflineTimeoutMs();

                if (offlineTimeoutMs <= 0) {
                    return self.$ref.root.update(updates);
                } else {
                    return Promise.race([
                        self.$ref.root.update(updates).then(function() { return true; }),
                        KayleeUtil.timeout(false, offlineTimeoutMs),
                    ]).then(function(completed) {
                        if (completed) {
                            onlineSetFailedCount = 0;
                        } else {
                            onlineSetFailedCount++;
                            log.warning('$set: ' + self.$nodeType + ': ' + self.$id + ': ' +
                                'update took too long, aborting wait');


                            if (onlineSetFailedCount >= 3) {
                                log.warning('updates taking too long, going offline');
                                offlineSetCompletedCount = 0;
                                doChangedEvent = false;
                                KayleeConnectionState.setWorkingOnline(false);
                                self.$notifyPreChangeListeners(self);
                                self.$notifyPostChangeListeners();
                            }
                        }
                    });
                }
            })
            .catch(function (err) {
                log.debug('updates = ' + JSON.stringify(updates, null, 4));
                log.error('$set on ' + self + ' failed, error: ' + err);
            })
            .then(function () {
                if (
                    !doChangedEvent ||
                    typeof self.$addUpdatesForChangedEvent === 'undefined' ||
                    Object.keys(changedData).length <= 0
                ) {
                    return Promise.resolve();
                }

                return self
                    .$addUpdatesForChangedEvent(
                        eventUpdates,
                        self.$id,
                        oldData,
                        changedData,
                        userInitiated
                    )
                    .then(function () {
                        return self.$ref.root.update(eventUpdates);
                    });
            })
            .catch(function (err) {
                log.debug('updates = ' + JSON.stringify(updates, null, 4));
                log.debug('eventUpdates = ' + JSON.stringify(eventUpdates, null, 4));
                log.error('$set on ' + self + ' failed, error: ' + err);
            });
    });
};

FirebaseObject.prototype.$getRefString = function (relative) {
    if (relative === true) {
        // remove the leading domain from the ref string to get a "relative" ref string
        return this.$ref.toString().replace(/^https?:\/\/[^/]*\//, '');
    }
    return this.$ref.toString();
};

module.exports = FirebaseObject;
